/**
 * Navbar - public site header ("Data Product" v3).
 * Clean sticky white nav: primary logo, the four public links, and one green
 * Sign-In CTA. Translucent white + blur, hairline bottom border. Light-only
 * (the public site has no theme toggle; the authed shell keeps its own).
 */

import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Box, Button, IconButton, Drawer, List, ListItem, ListItemText,
} from '@mui/material';
import { Menu as MenuIcon, Close as CloseIcon, East as EastIcon } from '@mui/icons-material';
import { useThemeMode } from '../../context/ThemeContext';

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'Markets', path: '/markets' },
  { label: 'Commodities', path: '/commodities' },
  { label: 'Currency Analysis', path: '/insights' },
];

function Navbar() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { palette } = useThemeMode();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const isActive = (path) => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <>
      <Box
        component="nav"
        sx={{
          position: 'sticky', top: 0, zIndex: 1100,
          bgcolor: palette.navBg,
          backdropFilter: 'saturate(180%) blur(14px)',
          borderBottom: `1px solid ${scrolled ? palette.navBorder : 'transparent'}`,
          transition: 'border-color 0.25s ease',
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2.5, md: 5 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 68 }}>
          {/* Logo */}
          <Box component={Link} to="/" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
            <Box
              component="img"
              src="/farmgate-logo-primary.svg"
              alt="Farmgate"
              sx={{ height: { xs: 32, md: 38 }, width: 'auto', display: 'block' }}
            />
          </Box>

          {/* Center nav */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
            {navLinks.map((link) => {
              const active = isActive(link.path);
              return (
                <Button
                  key={link.path}
                  component={Link} to={link.path}
                  disableRipple
                  sx={{
                    color: active ? palette.text : palette.textSecondary,
                    fontWeight: active ? 700 : 500,
                    fontSize: '0.9rem',
                    px: 1.6, py: 0.7, borderRadius: '8px', textTransform: 'none',
                    position: 'relative',
                    '&::after': active ? {
                      content: '""', position: 'absolute', left: 14, right: 14, bottom: 2,
                      height: 2, borderRadius: 2, bgcolor: palette.accent,
                    } : undefined,
                    transition: 'color 0.15s',
                    '&:hover': { color: palette.text, bgcolor: 'transparent' },
                  }}
                >
                  {link.label}
                </Button>
              );
            })}
          </Box>

          {/* Right side: CTA */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button
              component={Link} to="/login"
              sx={{
                display: { xs: 'none', md: 'inline-flex' },
                color: palette.text, fontWeight: 600, fontSize: '0.88rem',
                px: 1.6, py: 0.85, borderRadius: '10px', textTransform: 'none',
                '&:hover': { bgcolor: palette.surfaceHover },
              }}
            >
              Sign in
            </Button>
            <Button
              component={Link} to="/signup"
              endIcon={<EastIcon sx={{ fontSize: '14px !important' }} />}
              sx={{
                display: { xs: 'none', md: 'inline-flex' },
                bgcolor: palette.accent, color: '#fff', fontWeight: 600, fontSize: '0.88rem',
                px: 2.4, py: 0.85, borderRadius: '10px', textTransform: 'none',
                boxShadow: '0 1px 2px rgba(16,24,40,0.16)',
                '&:hover': { bgcolor: '#1C9550', boxShadow: palette.shadowMd },
                transition: 'background-color 0.2s, box-shadow 0.2s',
              }}
            >
              Create free account
            </Button>

            {/* Mobile menu */}
            <IconButton sx={{ display: { md: 'none' }, color: palette.text }} onClick={() => setMobileOpen(true)}>
              <MenuIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Mobile Drawer */}
      <Drawer anchor="right" open={mobileOpen} onClose={() => setMobileOpen(false)}
        PaperProps={{ sx: { width: '85%', maxWidth: 360, bgcolor: '#fff' } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${palette.divider}` }}>
            <Box
              component="img"
              src="/farmgate-logo-primary.svg"
              alt="Farmgate"
              sx={{ height: 34, width: 'auto', display: 'block' }}
            />
            <IconButton onClick={() => setMobileOpen(false)} sx={{ color: palette.textSecondary }}><CloseIcon /></IconButton>
          </Box>
          <Box sx={{ flex: 1, p: 2, overflowY: 'auto' }}>
            <List disablePadding>
              {navLinks.map((link) => (
                <ListItem key={link.path} component={Link} to={link.path} onClick={() => setMobileOpen(false)}
                  sx={{ borderRadius: '10px', mb: 0.5, bgcolor: isActive(link.path) ? palette.surface : 'transparent' }}>
                  <ListItemText primary={link.label}
                    primaryTypographyProps={{ fontWeight: isActive(link.path) ? 700 : 500, color: isActive(link.path) ? palette.text : palette.textSecondary }} />
                </ListItem>
              ))}
            </List>
          </Box>
          <Box sx={{ p: 3, borderTop: `1px solid ${palette.divider}` }}>
            <Button component={Link} to="/signup" fullWidth endIcon={<EastIcon />}
              sx={{ bgcolor: palette.accent, color: '#fff', fontWeight: 600, py: 1.2, borderRadius: '10px', textTransform: 'none', '&:hover': { bgcolor: '#1C9550' } }}>
              Create free account
            </Button>
            <Button component={Link} to="/login" fullWidth
              sx={{ mt: 1, color: palette.text, fontWeight: 600, py: 1.1, borderRadius: '10px', textTransform: 'none' }}>
              Sign in
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}

export default Navbar;
